import { useState, useEffect } from 'react';
import { useNotificationStore } from '../../store/notifications';
import type { Notification } from '../../store/notifications';

export default function AlertBanner() {
  const notifications = useNotificationStore((s) => s.notifications);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [visible, setVisible] = useState<Notification | null>(null);

  // Find the latest critical/error unread notification not yet dismissed
  useEffect(() => {
    const critical = notifications.find(
      (n) =>
        !n.read &&
        !dismissed.has(n.id) &&
        (n.type === 'error' || n.type === 'alert') &&
        n.category === 'alerts'
    );
    setVisible(critical || null);
  }, [notifications, dismissed]);

  if (!visible) return null;

  const isError = visible.type === 'error';
  const bgClass = isError
    ? 'bg-gradient-to-r from-red-900/80 to-red-800/60 border-red-500/30'
    : 'bg-gradient-to-r from-amber-900/80 to-amber-800/60 border-amber-500/30';
  const iconColor = isError ? 'text-red-400' : 'text-amber-400';
  const textColor = isError ? 'text-red-100' : 'text-amber-100';

  function handleDismiss() {
    setDismissed((prev) => new Set(prev).add(visible!.id));
  }

  function handleView() {
    if (visible?.link) {
      window.location.href = visible.link;
    } else {
      window.location.href = '/alerts';
    }
  }

  return (
    <div
      className={`w-full border-b px-4 py-2.5 flex items-center gap-3 ${bgClass}`}
      style={{ animation: 'slideDown 0.3s ease-out' }}
    >
      <style>{`
        @keyframes slideDown {
          from { transform: translateY(-100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>

      {/* Icon */}
      <svg className={`w-5 h-5 flex-shrink-0 ${iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
      </svg>

      {/* Message */}
      <p className={`flex-1 text-sm font-medium ${textColor} truncate`}>
        {visible.title}: {visible.message}
      </p>

      {/* Actions */}
      <button
        onClick={handleView}
        className="px-3 py-1 text-xs font-medium rounded bg-white/15 hover:bg-white/25 text-white transition-colors flex-shrink-0"
      >
        Voir
      </button>
      <button
        onClick={handleDismiss}
        className="p-1 rounded hover:bg-white/10 text-white/60 hover:text-white transition-colors flex-shrink-0"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}
